import { MessageSquareIcon } from "lucide-react";
import { useCallback } from "react";
import { T, RecordProps, TLShape, TLAsset, TLBinding, Editor } from "tldraw";

import {
  BlockBody,
  BlockContainer,
  NodeBlockHeader,
  BlockContainerShapeContext,
} from "@/components/canvas-editor/components/Container";
import { CanvasSimpleSelect } from "@/components/canvas-editor/components/Inputs";
import { NodeDocs } from "@/components/canvas-editor/NodeDocs";
import {
  BindableUtil,
  createMigrations,
} from "@/components/canvas-editor/shapes/bindable";
import {
  PostTypeShape,
  postTypeType,
  PostTypeOperator,
  postTypeOperators,
  PostTypeProps,
  metadataType,
  inclusionOperators,
  InclusionOperator,
  Dimensions,
} from "@/components/canvas-editor/shapes/shapes";
import { MIN_H, ToolStateNode } from "@/components/canvas-editor/utils";

export function isPostTypeShape(
  shape: TLAsset | TLBinding | TLShape,
): shape is PostTypeShape {
  return shape.type === postTypeType;
}

export class PostTypeTool extends ToolStateNode {
  static override id = postTypeType;

  static override uiToolDef = (editor: Editor) => ({
    id: postTypeType,
    icon: MessageSquareIcon,
    label: PostTypeShapeUtil.docs.title,
    kbd: "p",
    defaultColor: PostTypeShapeUtil.docs.defaultColor,
    onSelect: () => editor.setCurrentTool(postTypeType),
    docs: () => (
      <NodeDocs editor={editor} component={PostType} util={PostTypeShapeUtil} />
    ),
  });

  override onEnter() {
    this.editor.setCursor({ type: "none", rotation: 0 });
  }

  override onPointerDown() {
    const { currentPagePoint } = this.editor.inputs;

    this.editor.createShape({
      type: postTypeType,
      x: currentPagePoint.x,
      y: currentPagePoint.y,
    });

    this.editor.setCurrentTool("select");
  }
}

export class PostTypeShapeUtil extends BindableUtil<PostTypeShape> {
  static override migrations = createMigrations(postTypeType);
  static override type = postTypeType;

  override getType() {
    return postTypeType;
  }

  static override docs = {
    defaultColor: "blue" as const,
    title: "Post Type",
    description: "Select/Reject based on what type of post it is.",
  };

  static override props: RecordProps<PostTypeShape> = {
    w: T.number,
    minW: T.number,
    isCollapsed: T.boolean.optional(),
    resultState: T.literalEnum("success", "failure", "idle").optional(),
    isReadOnly: T.boolean.optional(),
    h: T.number,
    operator: T.literalEnum(...inclusionOperators),
    post_type: T.literalEnum(...postTypeOperators),
    metadata: metadataType,
  };

  override getDefaultProps() {
    return {
      w: 300,
      minW: 300,
      h: MIN_H,
      operator: inclusionOperators[0],
      post_type: postTypeOperators[0],
      metadata: undefined,
    };
  }

  override component(shape: PostTypeShape) {
    const selected = this.editor.getSelectedShapeIds();
    const isSelected = selected.includes(shape.id);

    return (
      <PostType
        shape={shape}
        updateShape={this.updateProps.bind(this)}
        isBound={this.getBoundToFeed(shape)}
        isSelected={isSelected}
      />
    );
  }
}

function PostType({
  isBound,
  shape,
  className,
  updateShape,
  isSelected,
}: {
  isSelected: boolean;
  isBound: boolean;
  shape: PostTypeShape;
  className?: string;
  updateShape: (shape: PostTypeShape, props: Partial<PostTypeProps>) => void;
}) {
  const variant = shape.props.operator === "in" ? "green" : "red";
  const onUpdateDimensions = useCallback(
    (dimensions: Pick<Dimensions, "h">) => {
      return updateShape(shape, dimensions);
    },
    [shape, updateShape],
  );

  return (
    <BlockContainerShapeContext value={shape}>
      <BlockContainer
        resultState={shape.props.resultState}
        pattern={shape.props.metadata?.pattern}
        isBound={isBound}
        className={className}
        variant={shape.props.metadata?.color || variant}
        isSelected={isSelected}
        onUpdateDimensions={onUpdateDimensions}
        dynamicHeight={true}
      >
        <NodeBlockHeader props={shape.props} docs={PostTypeShapeUtil.docs} />
        <BlockBody>
          <CanvasSimpleSelect
            aria-label="Comparison Operator"
            className="w-[120px]"
            value={shape.props.operator}
            options={[
              { label: "is", value: "in" as const },
              { label: "is not", value: "not_in" as const },
            ]}
            onValueChange={(v) =>
              updateShape(shape, { operator: v as InclusionOperator })
            }
          />
          <CanvasSimpleSelect
            aria-label="Post Type"
            tooltip="Type of post to select or reject upon"
            className="flex-1"
            value={shape.props.post_type}
            options={postTypeOperators}
            onValueChange={(v) =>
              updateShape(shape, { post_type: v as PostTypeOperator })
            }
          />
        </BlockBody>
      </BlockContainer>
    </BlockContainerShapeContext>
  );
}
